import { useState } from 'react';

import { AdminApiError, changeUserEmail, type ChangeEmailResult } from '@/lib/adminApi';

import { MIN_REASON_LENGTH } from './ResetPasswordDialog';

interface ChangeEmailDialogProps {
  userId: string;
  currentEmail?: string;
  onClose: () => void;
  onSuccess: (result: ChangeEmailResult) => void;
}

/**
 * Change a person's login email (ClickUp 869f4a4a0) — the same shape as
 * ResetPasswordDialog: one required reason, one server call, the server's
 * own sentence on refusal. The 409 for an address that already belongs to a
 * live account is the sentence worth reading: it tells the agent this is a
 * transfer, not a rename, and the page offers that as its own action.
 *
 * What the server does with the address is not decided here (an account with
 * a password keeps it; a Google-only account is handed a temporary password
 * by the existing reset mail) — the result says which path ran, and the page
 * shows it.
 */
export function ChangeEmailDialog({ userId, currentEmail, onClose, onSuccess }: ChangeEmailDialogProps) {
  const [newEmail, setNewEmail] = useState('');
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedReason = reason.trim();
  const trimmedEmail = newEmail.trim();
  const canSubmit =
    trimmedReason.length >= MIN_REASON_LENGTH && trimmedEmail.includes('@') && !sending;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSending(true);
    setError(null);
    try {
      const result = await changeUserEmail(userId, trimmedEmail, trimmedReason);
      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'Could not change the login email.');
      setSending(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Change login email"
      className="fixed inset-0 z-popover flex items-center justify-center bg-scrim/40 px-4"
    >
      <div className="w-full max-w-sm rounded-panel border border-border bg-surface-field p-6">
        <h2 className="text-h3 font-semibold text-fg-primary">Change login email</h2>
        <p className="mt-2 text-body text-fg-secondary">
          {currentEmail ? `Currently ${currentEmail}. ` : ''}
          Both the old and the new address will be notified. A Google-only account is
          given a temporary password by email.
        </p>

        <label className="mt-4 block text-meta font-medium text-fg-secondary" htmlFor="change-email-new">
          New email
        </label>
        <input
          id="change-email-new"
          type="email"
          value={newEmail}
          onChange={(event) => setNewEmail(event.target.value)}
          disabled={sending}
          autoComplete="off"
          className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary focus-visible:border-border-focus"
        />

        <label className="mt-4 block text-meta font-medium text-fg-secondary" htmlFor="change-email-reason">
          Reason (required, at least {MIN_REASON_LENGTH} characters)
        </label>
        <textarea
          id="change-email-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          disabled={sending}
          className="mt-1 w-full rounded-card border border-border bg-background px-3 py-2 text-body text-fg-primary focus-visible:border-border-focus"
        />

        {error && (
          <p
            role="alert"
            className="mt-3 rounded-card border border-destructive-border bg-destructive-soft px-3 py-2 text-meta text-destructive-soft-foreground"
          >
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="rounded-card px-4 py-2 text-body font-medium text-fg-secondary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? 'Changing…' : 'Change email'}
          </button>
        </div>
      </div>
    </div>
  );
}
